import { useCallback, useMemo, useState } from "react";
import {
  Container,
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Chip,
  Grid,
  TablePagination,
  InputAdornment,
  Stack,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import useApiEp from "../../hooks/useApiEp";
import DateRangePicker from "../../widgets/DateRangePicker";
import dayjs from "dayjs";
import { date } from "yup";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import SearchIcon from "@mui/icons-material/Search";
import { debounce } from "lodash";
import CampaignStatsPanel from "./AnalysisCom";
import { useNavigate, useSearchParams } from "react-router-dom";
import CustomPagination from "./CustomPagination";
import TopTitleActions from "../../widgets/TopTitleActions";
import { exportToExcel, getHeader, toastError } from "../../utils/util";
import { API_ENDPOINT } from "../../utils/constants";
import axios from "axios";
import CustomCircularP from "../../widgets/CustomCircularP";
import VisibilityIcon from "@mui/icons-material/Visibility";
const TrackMyCampaign = () => {
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [phoneS, setPhoneS] = useState("");
  const [status, setStatus] = useState("");
  const [debounceSq, setDebounceSq] = useState("");
  const [exporting, setExporting] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const navigate = useNavigate();
  const debouncedPhoneS = useCallback(
    debounce((value) => {
      setDebounceSq(value);
    }, 300),
    []
  );
  const [search] = useSearchParams();
  const rcsOption = search.get("isRcs");

  const {
    data: campaigns = [],
    isLoading: loadingCampaigns,
    isError: errorCampaigns,
    error: campaignError,
  } = useApiEp(`/api/get-campaigns?isRcs=${rcsOption === "true"}`);

  const {
    data: replyData,
    isLoading: loadingReplyCount,
    isError: errorReplyCount,
    error: replyCountError,
  } = useApiEp(
    `/api/my-reply-count?campaignName=${selectedCampaign}&isRcs=${
      rcsOption === "true"
    }`,
    !!selectedCampaign
  );

  const isDateRangeS = useMemo(
    () => dateRange.endDate && dateRange.startDate,
    [dateRange]
  );

  const {
    data: messagesData = {
      data: [],
      meta: { total: 0, page: 1, limit: 10, pages: 1 },
    },
    isLoading: loadingMessages,
    isError: errorMessages,
    error: messageError,
  } = useApiEp(
    selectedCampaign || isDateRangeS
      ? (() => {
          const params = new URLSearchParams();
          if (selectedCampaign) {
            params.append("campaignName", encodeURIComponent(selectedCampaign));
          }
          if (debounceSq) {
            params.append("phoneQ", encodeURIComponent(debounceSq));
          }
          params.append("page", String(page + 1));
          params.append("status", status);
          params.append("isRcs", rcsOption === "true");
          params.append("limit", String(rowsPerPage));
          if (isDateRangeS && dateRange?.startDate && dateRange?.endDate) {
            params.append("startDate", encodeURIComponent(dateRange.startDate));
            params.append("endDate", encodeURIComponent(dateRange.endDate));
          }
          // Construct URL
          return `/api/get-campaign-messages/?${params.toString()}`;
        })()
      : null,
    !!selectedCampaign || !!isDateRangeS
  );

  const handleCustomerSelect = (event, value) => {
    setSelectedCustomerId(value);
    setSelectedCampaign(null);
    setPage(0);
  };

  const handleCampaignSelect = (event, value) => {
    setSelectedCampaign(value);
    setPage(0);
  };

  const formatIST = (utcDate) => {
    return new Date(utcDate).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };
  const handleExport = async () => {
    if (exporting) {
      return;
    }
    const params = new URLSearchParams();
    if (selectedCampaign) {
      params.append("campaignName", encodeURIComponent(selectedCampaign));
    }
    params.append("status", status);
    params.append("isRcs", rcsOption === "true");
    if (isDateRangeS && dateRange?.startDate && dateRange?.endDate) {
      params.append("startDate", encodeURIComponent(dateRange.startDate));
      params.append("endDate", encodeURIComponent(dateRange.endDate));
    }
    const url = `${API_ENDPOINT}/api/get-messages-export?${params.toString()}`;
    try {
      setExporting(true);
      const response = await axios.get(url, getHeader());
      exportToExcel(response.data);
    } catch (error) {
      toastError(error);
    } finally {
      setExporting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "read":
      case "delivered":
        return "success";
      case "sent":
        return "info";
      case "queued":
        return "warning";
      default:
        return "error";
    }
  };

  return (
    <Container sx={{ py: 4 }}>
      <TopTitleActions
        title={"Track Campaign"}
        actions={[
          {
            icon: <ImportExportIcon />,
            label: "Export Data",
            action: handleExport,
          },
        ]}
      />
      <CustomCircularP show={exporting} />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            options={campaigns}
            loading={loadingCampaigns}
            value={selectedCampaign}
            onChange={handleCampaignSelect}
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Campaign"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCampaigns && (
                        <CircularProgress color="inherit" size={16} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {errorCampaigns && (
            <Typography color="error" variant="caption">
              Error: {campaignError?.message || "Failed to load campaigns"}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DateRangePicker
            startLabel="From"
            endLabel="To"
            onChange={(newRange) => {
              console.log(new Date(newRange.startDate));
              setDateRange(newRange);
            }}
            format="DD/MM/YYYY"
            showQuickSelect={true}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            placeholder="Search Phone"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            value={phoneS}
            variant="outlined"
            size="small"
            onChange={(e) => {
              const value = e.target.value;
              setPhoneS(value);
              debouncedPhoneS(value);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl size="small" fullWidth>
            <InputLabel>Message Status</InputLabel>
            <Select
              value={status}
              label="Message Status"
              onChange={(e) => {
                setStatus(e.target.value);
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value={"queued"}>Queued</MenuItem>
              <MenuItem value={"cancelled"}>Cancelled</MenuItem>
              <MenuItem value={"sent"}>Sent</MenuItem>
              <MenuItem value={"delivered"}>Delivered</MenuItem>
              <MenuItem value={"read"}>Read</MenuItem>
              <MenuItem value={"failed"}>Failed</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Stack direction={"row"} gap={2}>
        {!!(selectedCampaign || isDateRangeS) && (
          <CampaignStatsPanel
            campaignName={selectedCampaign}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
          />
        )}
        {selectedCampaign && replyData && (
          <Chip
            label={`${replyData.replyCount} replies`}
            onClick={() => {
              const newTabUrl = `/home/chats?tn=${selectedCampaign}`;
              window.open(newTabUrl, "_blank");
            }}
            color={replyData.replyCount ? "success" : "warning"}
            sx={{ cursor: "pointer", mt: 1 }}
          />
        )}
      </Stack>

      <TableContainer component={Paper} sx={{ mt: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Message ID</TableCell>
              <TableCell>Recipient</TableCell>
              <TableCell>Template</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Updated At</TableCell>
              <TableCell>Detail</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingMessages && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            )}
            {errorMessages && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="error">
                    Error: {messageError?.message || "Failed to load messages"}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loadingMessages &&
              !errorMessages &&
              messagesData.data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography>
                      {selectedCustomerId && selectedCampaign
                        ? "No messages found"
                        : "Select a customer and campaign to view messages"}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            {!loadingMessages &&
              !errorMessages &&
              messagesData.data.map((message) => (
                <TableRow key={message._id}>
                  <TableCell>{message._id}</TableCell>
                  <TableCell>{message.to}</TableCell>
                  <TableCell>{message.templateName}</TableCell>
                  <TableCell>
                    <Chip
                      label={message.status}
                      color={getStatusColor(message.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatIST(message.updatedOn)}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => {
                        navigate(`/home/${message._id}/message-detail`);
                      }}
                      size="small"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={messagesData.meta.total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      /> */}
      <Box display={"flex"} justifyContent={"center"} mt={2}>
        <CustomPagination
          messagesData={messagesData}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
        />
      </Box>
    </Container>
  );
};

export default TrackMyCampaign;
